import {FC, useContext, useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@mui/material';
import {ObraPresupuesto, WandaProject} from '../../../types/ObraDetails';
import InformationIcon from '../components/informationIcon/informationIcon';
import styles from './budget.module.scss';
import { AuthContext } from '../../../services/authContextProvider';
import Section from './components/section';
import ObrasAPI from '../../../services/APIObras';
import ErrorDetails from '../components/ErrorDetails/ErrorDetails';
import BudgetLoadingSkeleton from './components/budgetLoadingSkeleton/budgetLoadingSkeleton';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface BudgetProps {
  codCentro: string;
  wandaProject?: WandaProject;
  wandaProjectLoading: boolean;
  wandaProjectError: boolean;
}

/* eslint-disable @typescript-eslint/no-non-null-assertion */
const Budget: FC<BudgetProps> = (props) => {
  const { codCentro, wandaProject, wandaProjectLoading, wandaProjectError  } = props;
  const [t] = useTranslation();
  const authContext = useContext(AuthContext);
  const [budget, setBudget] = useState<ObraPresupuesto>();
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const api = ObrasAPI.getInstance();

    setBudget(undefined);
    setLoadError(false);

    if (!wandaProject) return;

    api.getObraBudgetAPI(codCentro, wandaProject)
      .then((budgetResponse) => {
        setBudget(budgetResponse);
      })
      .catch(() => {
        setLoadError(true);
      });
  }, [codCentro, wandaProject]);

  if ((!budget && loadError) || wandaProjectError) return <ErrorDetails />;
  if (!budget || wandaProjectLoading) return <BudgetLoadingSkeleton />;

  const prepareBudget = (budget: number, moneda: string): string => {
    if (!budget) return t('details.notInformed');
    const [wholeNumber, decimal] = budget.toString().split('.');
    return `${wholeNumber.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}${decimal ? ',' + decimal.substring(0, 2) : ',00'} ` + moneda;
  };

  const isViewer = !authContext.can('can_read_sensitive');
  const hasLocalCurrency = Boolean(budget.monedaLocal);
  const localCurrencyLabel = budget.monedaLocal || t('details.notInformed');

  const parseCurrencyValue = (value:number): string => (value?.toFixed(5).toString().replace('.',','));

  return (
    <div className={styles.budgetContainer} data-testid='budget-test'>
      <Grid container direction="column" mt={2}>
        <Grid item container spacing={2} justifyContent="space-between" alignItems="stretch" xs>
          <Grid item xs>
            <Typography id='localCurrency-title' className={styles.heading}>
              {t('details.budget.localCurrency')}:
              <span className={styles.localCurrency} id='localCurrency'>{localCurrencyLabel}</span>
              <InformationIcon message={t('help.budget.localCurrency')} />
            </Typography>
          </Grid>
          <Grid item xs="auto">
            <Typography id='noTaxes' className={styles.noTaxes}>
              {t('details.budget.noTaxes')}
            </Typography>
          </Grid>
        </Grid>
        <Section
          id='awardAmount'
          idTitle='awardAmount-title'
          title={t('details.budget.awardAmount')}
          infoMsg={t('help.budget.awardAmount')}
          date={{
            id: 'awardDate',
            item: {
              title: t('details.budget.awardDate'),
              value: budget.fecAdjudicacion || t('details.notInformed')
            }
          }}
          amount={{
            id:'awardAmount',
            item: {
              title: t('details.budget.awardAmount'),
              value: `${prepareBudget(budget.importeAdjudicacion.monedaEur, 'EUR')}`
            },
            switchItem: `${prepareBudget(budget.importeAdjudicacion.monedaLocal, budget.monedaLocal)}`,
            switchData:(budget.importeAdjudicacion.monedaEur && hasLocalCurrency && budget.monedaLocal !== 'EUR') ? true : false
          }}
          accionaAmount={{
            id:'accionaAwardAmount',
            item:{ title: t('details.budget.accionaAwardAmount'), value: `${prepareBudget(budget.importeAdjudicacion.accionaMonedaEur, 'EUR')}` },
            switchItem:`${prepareBudget(budget.importeAdjudicacion.accionaMonedaLocal, budget.monedaLocal)}`,
            switchData: (budget.importeAdjudicacion.monedaEur && hasLocalCurrency && budget.monedaLocal !== 'EUR') ? true : false
          }}
          currencyExchange={{
            id: 'award-currencyExchange',
            item: {
              title: t('details.budget.currencyExchange'),
              value: budget.importeAdjudicacion.monedaEur && budget.monedaLocal ? (budget.monedaLocal === 'EUR' ? '-' : `1 EUR = ${parseCurrencyValue(budget.importeAdjudicacion.tipoCambio)} ${budget.monedaLocal}`) : '-'
            }
          }}
        />

        {isViewer || <>
          <Section
            id='updatedAmount'
            idTitle='updatedAmount-title'
            title={t('details.budget.updatedApprovedAmount')}
            infoMsg={t('help.budget.updatedApprovedAmount')}
            date={{
              id:'approvalDate',
              item: { title: t('details.budget.approvalDate'), value: budget.fecAprobacionEconomica || t('details.notInformed') }
            }}
            amount={{
              id:'updatedAmount',
              item: { title: t('details.budget.updatedAmount'), value: `${prepareBudget(budget.importeActualizado.monedaEur, 'EUR')}` },
              switchItem: `${prepareBudget(budget.importeActualizado.monedaLocal, budget.monedaLocal)}`,
              switchData: (budget.importeActualizado.monedaEur && hasLocalCurrency && budget.monedaLocal !== 'EUR') ? true : false
            }}
            accionaAmount={{
              id:'accionaUpdatedAmount',
              item: { title: t('details.budget.accionaUpdatedAmount'), value: `${prepareBudget(budget.importeActualizado.accionaMonedaEur, 'EUR')}` },
              switchItem: `${prepareBudget(budget.importeActualizado.accionaMonedaLocal, budget.monedaLocal)}`,
              switchData: (budget.importeActualizado.accionaMonedaEur && budget.monedaLocal !== 'EUR') ? true : false
            }}
            currencyExchange={{
              id: 'updated-currencyExchange',
              item: {
                title: t('details.budget.currencyExchange'),
                value: budget.importeActualizado.monedaEur && budget.monedaLocal ? (budget.monedaLocal === 'EUR' ? '-' : `1 EUR = ${parseCurrencyValue(budget.importeActualizado.tipoCambio)} ${budget.monedaLocal}`) : '-'
              }
            }}
          />

          <Section
            id='finalAmount'
            idTitle='finalAmount-title'
            title={t('details.budget.finalAmountSection')}
            infoMsg={t('help.budget.finalAmount')}
            date={{
              id:'finalDate',
              item: { title: t('details.budget.finalDate'), value: budget.fecAprobacionEconomicaFinal || t('details.notInformed') }
            }}
            amount={{
              id:'finalAmount',
              item: { title: t('details.budget.finalAmount'), value: `${prepareBudget(budget.importeActualizadoFinal.monedaEur, 'EUR')}` },
              switchItem: `${prepareBudget(budget.importeActualizadoFinal.monedaLocal, budget.monedaLocal)}`,
              switchData: (budget.importeActualizadoFinal.monedaEur && hasLocalCurrency && budget.monedaLocal !== 'EUR') ? true : false
            }}
            accionaAmount={{
              id:'accionaFinalAmount',
              item: { title: t('details.budget.accionaFinalAmount'), value: `${prepareBudget(budget.importeActualizadoFinal.accionaMonedaEur, 'EUR')}` },
              switchItem: `${prepareBudget(budget.importeActualizadoFinal.accionaMonedaLocal, budget.monedaLocal)}`,
              switchData: (budget.importeActualizadoFinal.accionaMonedaEur && budget.monedaLocal !== 'EUR') ? true : false
            }}
            currencyExchange={{
              id: 'final-currencyExchange',
              item: {
                title: t('details.budget.currencyExchange'),
                value: budget.importeActualizadoFinal.monedaEur && budget.monedaLocal ? (budget.monedaLocal === 'EUR' ? '-' : `1 EUR = ${parseCurrencyValue(budget.importeActualizadoFinal.tipoCambio)} ${budget.monedaLocal}`) : '-'
              }
            }}
          />
        </>}
      </Grid>
      <div className='footer' style={{marginBottom: '75px'}}></div>
    </div>
  );
};

export default Budget;
